import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { Dropdown } from 'components/ui/Dropdown'
import { teamsApi, offersApi, type Team, type Offer } from 'lib/api'
import { formatDate } from 'lib/utils'
import { PageHeader } from 'components/layout/PageHeader'
import { 
  Plus, 
  Search, 
  Users, 
  Edit3, 
  Trash2, 
  Calendar,
  UserMinus,
  Archive,
  ArchiveRestore,
  Briefcase,
  Grid3x3,
  List,
  Filter,
  CheckSquare,
  CheckCircle2 as CheckCircle,
  X
} from 'lucide-react'
import { useToast } from 'contexts/ToastContext'
import { useNavigate, useLocation } from 'react-router-dom'

interface TeamsProps {
  hideHeader?: boolean
  onCreateTeamClick?: () => void
  onTeamCreated?: () => void
}

export function Teams({ hideHeader = false, onCreateTeamClick, onTeamCreated }: TeamsProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'archived' | 'active'>('active')
  const [offersFilter, setOffersFilter] = useState<'all' | 'withOffers' | 'withoutOffers'>('all')
  const [contentFilter, setContentFilter] = useState<'all' | 'empty' | 'nonEmpty'>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null)
  const [offers, setOffers] = useState<Offer[]>([])
  const [offersLoading, setOffersLoading] = useState(false)
  const [archivingIds, setArchivingIds] = useState<Set<number>>(new Set())
  const [offerModalTeam, setOfferModalTeam] = useState<Team | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedTeams, setSelectedTeams] = useState<Set<number>>(new Set())
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [deletingBulk, setDeletingBulk] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(12)



  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true)
      // Load all pages — getAll() only returns PAGE_SIZE (20)
      const teamsList = await teamsApi.getAllPages()
      setTeams(teamsList || [])
      // Ensure offers are fetched only after teams (candidates) are populated
      fetchOffers()
    } catch (error) {
      console.error('Error fetching teams:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teams.messages.teamFetchFailed'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast])

  const fetchOffers = useCallback(async () => {
    try {
      setOffersLoading(true)
      const offersList = await offersApi.getAllPages({ light: 'true' })
      setOffers(offersList || [])
    } catch (error) {
      console.error('Error fetching offers:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teams.messages.offerFetchFailed'),
        duration: 5000
      })
    } finally {
      setOffersLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast])

  const fetchedRef = useRef(false)
  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    fetchTeams()
    // `fetchOffers()` is now handled inside `fetchTeams()` to sequence it
  }, [fetchTeams])

  const filteredTeams = teams.filter(team => {
    const matchesSearch =
      (team.name && team.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (team.description && team.description.toLowerCase().includes(searchTerm.toLowerCase()))

    if (!matchesSearch) return false

    const offersCount = team.offers?.length || team.offer_ids?.length || 0
    const candidatesCount = team.candidates?.length || 0
    
    // Status filter (active / archived)
    if (statusFilter === 'archived' && !team.is_archived) return false
    if (statusFilter === 'active' && team.is_archived) return false

    // Offers filter
    if (offersFilter === 'withOffers' && offersCount === 0) return false
    if (offersFilter === 'withoutOffers' && offersCount > 0) return false

    // Content filter (empty / non-empty)
    if (contentFilter === 'empty' && candidatesCount > 0) return false
    if (contentFilter === 'nonEmpty' && candidatesCount === 0) return false

    return true
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredTeams.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentTeams = filteredTeams.slice(startIndex, endIndex)
  const totalGroups = teams.length
  const offerLinkedGroups = teams.filter(team => (team.offers?.length || team.offer_ids?.length || 0) > 0).length
  const archivedGroups = teams.filter(team => team.is_archived).length
  const emptyGroups = teams.filter(team => (team.candidates?.length || 0) === 0).length

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Reset to first page when search / filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, offersFilter, contentFilter])

  const handleCreateTeam = (newTeam: Team) => {
    setTeams(prev => [newTeam, ...prev])
    setShowCreateModal(false)
    addToast({
      type: 'success',
      title: t('common.success'),
      message: t('teams.messages.teamCreated'),
      duration: 3000
    })
    // Refresh teams list to ensure consistency
    fetchTeams()
    // Notify parent component to refresh if callback provided
    if (onTeamCreated) {
      onTeamCreated()
    }
  }

  const handleEditTeam = (updatedTeam: Team) => {
    setTeams(prev => prev.map(team => 
      team.id === updatedTeam.id ? updatedTeam : team
    ))
    setShowEditModal(false)
    setEditingTeam(null)
    addToast({
      type: 'success',
      title: t('common.success'),
      message: t('teams.messages.teamUpdated'),
      duration: 3000
    })
  }

  const handleDeleteTeam = async () => {
    if (!deletingTeam) return

    try {
      await teamsApi.delete(deletingTeam.id)
      setTeams(prev => prev.filter(team => team.id !== deletingTeam.id))
      setShowDeleteConfirm(false)
      setDeletingTeam(null)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('teams.messages.teamDeleted'),
        duration: 3000
      })
    } catch (error) {
      console.error('Error deleting team:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teams.messages.teamDeleteFailed'),
        duration: 5000
      })
    }
  }

  const toggleSelectTeam = (teamId: number) => {
    setSelectedTeams(prev => {
      const next = new Set(prev)
      if (next.has(teamId)) {
        next.delete(teamId)
      } else {
        next.add(teamId)
      }
      return next
    })
  }

  const toggleSelectAllTeams = () => {
    setSelectedTeams(prev => {
      if (prev.size === currentTeams.length) {
        return new Set()
      }
      return new Set(currentTeams.map(team => team.id))
    })
  }

  const toggleSelectionMode = () => {
    setSelectionMode(prev => {
      const next = !prev
      if (!next) {
        setSelectedTeams(new Set())
      }
      return next
    })
  }

  const handleBulkDeleteTeams = async () => {
    if (selectedTeams.size === 0) return

    setDeletingBulk(true)
    const idsToDelete = Array.from(selectedTeams)
    let successCount = 0

    try {
      for (const id of idsToDelete) {
        try {
          await teamsApi.delete(id)
          successCount += 1
        } catch (error) {
          console.error('Error deleting team in bulk:', error)
        }
      }

      if (successCount > 0) {
        setTeams(prev => prev.filter(team => !selectedTeams.has(team.id)))
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('teams.bulkDeleteSuccess', { count: successCount, defaultValue: `${successCount} groups deleted` }),
          duration: 4000
        })
      } else {
        addToast({
          type: 'error',
          title: t('common.error'),
          message: t('teams.bulkDeleteFailed', 'Failed to delete selected groups. Please try again.'),
          duration: 5000
        })
      }

      setSelectedTeams(new Set())
      setSelectionMode(false)
      setShowBulkDeleteModal(false)
    } finally {
      setDeletingBulk(false)
    }
  }

  const toggleArchiveStatus = async (team: Team) => {
    setArchivingIds(prev => new Set(prev).add(team.id))

    try {
      const response = await teamsApi.update(team.id, { is_archived: !team.is_archived })
      setTeams(prev => prev.map(item => item.id === team.id ? response.data : item))
      addToast({
        type: 'success',
        title: t('common.success'),
        message: team.is_archived
          ? t('teams.messages.teamUnarchived')
          : t('teams.messages.teamArchived'),
        duration: 3000
      })
    } catch (error) {
      console.error('Error toggling archive state:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teams.messages.teamArchiveFailed'),
        duration: 5000
      })
    } finally {
      setArchivingIds(prev => {
        const next = new Set(prev)
        next.delete(team.id)
        return next
      })
    }
  }

  const openEditModal = (team: Team) => {
    setEditingTeam(team)
    setShowEditModal(true)
  }

  const openDeleteConfirm = (team: Team) => {
    setDeletingTeam(team)
    setShowDeleteConfirm(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-[1149px]:px-4 max-[1149px]:py-4 max-[1149px]:space-y-4">
      {!hideHeader && (
        <PageHeader
          title={t('teams.title')}
          actions={
            <Button onClick={() => onCreateTeamClick ? onCreateTeamClick() : setShowCreateModal(true)} className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm">
              <Plus className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
              <span className="max-[1149px]:text-sm">{t('teams.createTeam')}</span>
            </Button>
          }
        />
      )}

      {/* Stats Cards */}
      <div className="grid gap-3 md:grid-cols-4 sm:grid-cols-2">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('teams.stats.totalTeams')}</p>
                <p className="text-xl font-bold">{totalGroups}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900">
                <Briefcase className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('teams.stats.offerLinkedTeams')}</p>
                <p className="text-xl font-bold">{offerLinkedGroups}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900">
                <Archive className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('teams.stats.archivedTeams')}</p>
                <p className="text-xl font-bold">{archivedGroups}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
                <UserMinus className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('teams.stats.emptyTeams')}</p>
                <p className="text-xl font-bold">{emptyGroups}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Archived groups pill / chip */}
      {archivedGroups > 0 && (
        <div className="flex justify-end -mt-2 max-[1149px]:mt-0">
          <button
            type="button"
            onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Archive className="h-3.5 w-3.5" />
            {statusFilter === 'active'
              ? t('teams.archivedHiddenChip', { count: archivedGroups })
              : t('teams.archivedShownChip', { count: archivedGroups })}
          </button>
        </div>
      )}

      {/* Search + selection + view mode + filters */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('teams.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Selection mode & bulk actions (icon-only) */}
          {currentTeams.length > 0 && (
            <>
              <Button
                variant={selectionMode ? 'default' : 'outline'}
                size="sm"
                onClick={toggleSelectionMode}
                className="h-9 w-9 p-0 flex items-center justify-center transition-all"
                title={
                  selectionMode
                    ? t('teams.selection.cancel', 'Cancel selection')
                    : t('teams.selection.enable', 'Select groups')
                }
              >
                {selectionMode ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
              </Button>
              {selectionMode && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAllTeams}
                    className="h-9 w-9 p-0 flex items-center justify-center transition-all"
                    title={
                      selectedTeams.size === currentTeams.length && currentTeams.length > 0
                        ? t('teams.selection.deselectAll', 'Deselect all')
                        : t('teams.selection.selectAll', 'Select all')
                    }
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                  {selectedTeams.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowBulkDeleteModal(true)}
                      className="h-9 w-9 p-0 flex items-center justify-center transition-all"
                      title={t('teams.selection.deleteSelected', { count: selectedTeams.size, defaultValue: 'Delete selected' })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </>
          )}

          {/* View toggle (grid / list) */}
          <div className="flex items-center gap-0.5 border border-input rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              title={t('teams.gridView', 'Grid view')}
            >
              <Grid3x3 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              title={t('teams.listView', 'List view')}
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {/* Filters dropdown */}
          <Dropdown
            trigger={
              <Button variant="outline" size="sm" className="whitespace-nowrap">
                <Filter className="h-4 w-4 mr-2" />
                <span className="text-sm">{t('teams.filters', 'Filtres')}</span>
              </Button>
            }
          >
            <div className="px-3 py-2 space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  {t('teams.filters', 'Filtres')}
                </p>
                {/* Status filter */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium mb-1">
                    {t('teams.statusFilter.title', 'Statut du groupe')}
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'archived')}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none"
                  >
                    <option value="all">{t('teams.statusFilter.all', 'Tous')}</option>
                    <option value="active">{t('teams.statusFilter.active', 'Actifs')}</option>
                    <option value="archived">{t('teams.statusFilter.archived', 'Archivés')}</option>
                  </select>
                </div>

                {/* Offers filter */}
                <div className="space-y-1.5 mt-3">
                  <label className="block text-xs font-medium mb-1">
                    {t('teams.offersFilter.title', 'Offres associées')}
                  </label>
                  <select
                    value={offersFilter}
                    onChange={(e) => setOffersFilter(e.target.value as 'all' | 'withOffers' | 'withoutOffers')}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none"
                  >
                    <option value="all">{t('teams.offersFilter.all', 'Toutes')}</option>
                    <option value="withOffers">{t('teams.offersFilter.withOffers', 'Avec offres')}</option>
                    <option value="withoutOffers">{t('teams.offersFilter.withoutOffers', 'Sans offres')}</option>
                  </select>
                </div>

                {/* Content filter (members) */}
                <div className="space-y-1.5 mt-3">
                  <label className="block text-xs font-medium mb-1">
                    {t('teams.contentFilter.title', 'Contenu du groupe')}
                  </label>
                  <select
                    value={contentFilter}
                    onChange={(e) => setContentFilter(e.target.value as 'all' | 'empty' | 'nonEmpty')}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none"
                  >
                    <option value="all">{t('teams.contentFilter.all', 'Tous')}</option>
                    <option value="empty">{t('teams.contentFilter.empty', 'Sans candidats')}</option>
                    <option value="nonEmpty">{t('teams.contentFilter.nonEmpty', 'Avec candidats')}</option>
                  </select>
                </div>
              </div>

              {(searchTerm || statusFilter !== 'active' || offersFilter !== 'all' || contentFilter !== 'all') && (
                <div className="pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('')
                      setStatusFilter('active')
                      setOffersFilter('all')
                      setContentFilter('all')
                    }}
                    className="w-full"
                  >
                    {t('common.clearFilters', 'Effacer les filtres')}
                  </Button>
                </div>
              )}
            </div>
          </Dropdown>
        </div>
      </div>

      {/* Teams Grid / List */}
      {currentTeams.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center max-[1149px]:p-8">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
            <p className="text-muted-foreground text-lg max-[1149px]:text-base">{t('teams.noTeamsFound')}</p>
            <p className="text-sm text-muted-foreground mt-1 max-[1149px]:text-xs">
              {filteredTeams.length === 0 
                ? (searchTerm 
                    ? t('teams.noTeamsFoundDesc')
                    : t('teams.noTeamsFoundEmpty'))
                : t('teams.noTeamsOnPage')
              }
            </p>
            {filteredTeams.length === 0 && !searchTerm && (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="mt-4 max-[1149px]:mt-3 max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                <Plus className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
                <span className="max-[1149px]:text-sm">{t('teams.createFirstTeam')}</span>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {currentTeams.map((team) => {
            const offerCount = team.offers?.length || 0

            return (
            <Card
              key={team.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (selectionMode) {
                  toggleSelectTeam(team.id)
                } else {
                  navigate(`/teams/${team.id}/members`)
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  if (selectionMode) {
                    toggleSelectTeam(team.id)
                  } else {
                    navigate(`/teams/${team.id}/members`)
                  }
                }
              }}
              className={`transition-all cursor-pointer group overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                selectionMode && selectedTeams.has(team.id)
                  ? 'ring-2 ring-primary shadow-md bg-primary/5 border-primary/50'
                  : selectionMode
                  ? 'hover:shadow-md hover:ring-1 hover:ring-primary/30 border-transparent'
                  : 'hover:shadow-lg'
              } ${team.is_archived ? 'opacity-70 border-dashed border-muted-foreground/40' : ''}`}
            >
              <CardContent className="p-3 overflow-hidden">
                {/* Header with color indicator */}
                <div className="flex items-start mb-2">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div 
                      className="w-10 h-10 rounded-full flex-shrink-0 border-2 border-border flex items-center justify-center"
                      style={{ backgroundColor: team.color }}
                    >
                      <Users className="h-5 w-5 text-white drop-shadow-sm" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm text-foreground truncate">
                        {team.name}
                      </h3>
                      {team.description && (
                        <p className="text-xs text-muted-foreground truncate">
                          {team.description}
                        </p>
                      )}
                      {team.is_archived && (
                        <Badge variant="outline" className="mt-0.5 text-[9px] uppercase tracking-wide px-1 py-0">
                          {t('teams.archivedBadge')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Team info */}
                <div className="space-y-0.5 mb-2">
                  <div className="flex items-center space-x-1 text-[10px] text-muted-foreground">
                    <Users className="h-2.5 w-2.5 flex-shrink-0" />
                    <span className="truncate min-w-0 flex-1">
                      {team.candidates?.length || 0} {t('teams.card.candidates')}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 text-[10px] text-muted-foreground">
                    <Calendar className="h-2.5 w-2.5 flex-shrink-0" />
                    <span className="truncate min-w-0 flex-1">{formatDate(team.created_at)}</span>
                  </div>
                </div>

                {/* Members preview */}
                {team.candidates && team.candidates.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mb-2">
                    {team.candidates.slice(0, 2).map((candidate) => (
                      <Badge key={candidate.id} variant="secondary" className="text-[10px] px-1.5 py-0.5">
                        {candidate.name.split(' ')[0]}
                      </Badge>
                    ))}
                    {team.candidates.length > 2 && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                        +{team.candidates.length - 2}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-1.5 pt-1.5 border-t border-border">
                  <Button
                    variant={team.is_archived ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation()
                      toggleArchiveStatus(team)
                    }}
                    className="h-7 px-2"
                    title={team.is_archived ? t('teams.unarchive') : t('teams.archive')}
                    disabled={archivingIds.has(team.id)}
                  >
                    {archivingIds.has(team.id) ? (
                      <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    ) : team.is_archived ? (
                      <ArchiveRestore className="h-3 w-3" />
                    ) : (
                      <Archive className="h-3 w-3" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation()
                      openEditModal(team)
                    }}
                    className="h-7 px-2"
                  >
                    <Edit3 className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation()
                      openDeleteConfirm(team)
                    }}
                    className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            )
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {selectionMode && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2 bg-muted/30 border border-border rounded-lg">
              <CheckSquare className="h-3.5 w-3.5 text-primary" />
              <span>{t('teams.selection.hint', 'Click on groups to select them')}</span>
              {selectedTeams.size > 0 && (
                <span className="ml-auto font-medium text-foreground">
                  {selectedTeams.size} {t('teams.selection.selected', 'selected')}
                </span>
              )}
            </div>
          )}
          {currentTeams.map((team) => {
            return (
              <Card
                key={team.id}
                role="button"
                tabIndex={0}
              onClick={() => {
                if (selectionMode) {
                  toggleSelectTeam(team.id)
                } else {
                  navigate(`/teams/${team.id}/members`)
                }
              }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                  if (selectionMode) {
                    toggleSelectTeam(team.id)
                  } else {
                    navigate(`/teams/${team.id}/members`)
                  }
                  }
                }}
              className={`transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                selectionMode && selectedTeams.has(team.id)
                  ? 'ring-2 ring-primary shadow-md bg-primary/5 border-primary/50'
                  : selectionMode
                  ? 'hover:bg-muted/30 hover:shadow-sm hover:border-primary/20 border-transparent'
                  : 'hover:bg-muted/50 hover:shadow-sm'
              } ${team.is_archived ? 'opacity-70 border-dashed border-muted-foreground/40' : ''}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div 
                        className="h-10 w-10 rounded-full flex-shrink-0 border-2 border-border flex items-center justify-center"
                        style={{ backgroundColor: team.color }}
                      >
                        <Users className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base truncate">{team.name}</h3>
                        {team.description && (
                          <p className="text-sm text-muted-foreground truncate">{team.description}</p>
                        )}
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs text-muted-foreground truncate">
                            {team.candidates?.length || 0} {t('teams.card.candidates')}
                          </span>
                          <span className="text-xs text-muted-foreground truncate flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(team.created_at)}</span>
                          </span>
                          {team.is_archived && (
                            <Badge variant="outline" className="text-xs">
                              {t('teams.archivedBadge')}
                            </Badge>
                          )}
                        </div>
                        {team.candidates && team.candidates.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {team.candidates.slice(0, 4).map((candidate) => (
                              <Badge key={candidate.id} variant="secondary" className="text-xs px-1.5 py-0.5">
                                {candidate.name.split(' ')[0]}
                              </Badge>
                            ))}
                            {team.candidates.length > 4 && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                                +{team.candidates.length - 4}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <Button
                        variant={team.is_archived ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation()
                          toggleArchiveStatus(team)
                        }}
                        className="h-8 text-xs px-2"
                        title={team.is_archived ? t('teams.unarchive') : t('teams.archive')}
                        disabled={archivingIds.has(team.id)}
                      >
                        {archivingIds.has(team.id) ? (
                          <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        ) : team.is_archived ? (
                          <ArchiveRestore className="h-4 w-4" />
                        ) : (
                          <Archive className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation()
                          openEditModal(team)
                        }}
                        className="h-8 text-xs px-2"
                      >
                        {t('candidates.edit')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation()
                          openDeleteConfirm(team)
                        }}
                        className="h-8 text-xs px-2 dark:text-red-400 dark:hover:text-red-300"
                      >
                        {t('candidates.delete')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 max-[1149px]:space-x-1 max-[1149px]:flex-wrap max-[1149px]:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="max-[1149px]:h-8 max-[1149px]:text-xs max-[1149px]:px-3"
          >
            <span className="max-[1149px]:text-xs">{t('teams.previous')}</span>
          </Button>
          
          <div className="flex items-center space-x-1 max-[1149px]:space-x-0.5">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  className="w-8 h-8 p-0 max-[1149px]:w-7 max-[1149px]:h-7 max-[1149px]:text-xs"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="max-[1149px]:h-8 max-[1149px]:text-xs max-[1149px]:px-3"
          >
            <span className="max-[1149px]:text-xs">{t('teams.next')}</span>
          </Button>
        </div>
      )}

      {/* Bulk Delete Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => {
          if (!deletingBulk) setShowBulkDeleteModal(false)
        }}
        title={t('teams.bulkDelete.title', 'Delete Multiple Groups')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              'teams.bulkDelete.message',
              {
                count: selectedTeams.size,
                defaultValue: `Are you sure you want to delete ${selectedTeams.size} group(s)? This action cannot be undone.`
              }
            )}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteModal(false)}
              disabled={deletingBulk}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteTeams}
              disabled={deletingBulk}
            >
              {deletingBulk ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.deleting', 'Deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('teams.bulkDelete.confirm', 'Delete All')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Team Modal */}
      <CreateTeamModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTeamCreated={handleCreateTeam}
        offers={offers}
        offersLoading={offersLoading}
      />

      {/* Edit Team Modal */}
      {editingTeam && (
        <EditTeamModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setEditingTeam(null)
          }}
          team={editingTeam}
          onTeamUpdated={handleEditTeam}
          offers={offers}
          offersLoading={offersLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setDeletingTeam(null)
        }}
        title={t('teams.deleteTeamTitle', 'Delete Team')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('teams.deleteTeamConfirm', { name: deletingTeam?.name })}
          </p>
          {deletingTeam?.candidates && deletingTeam.candidates.length > 0 && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {t('teams.deleteTeamWarning', { count: deletingTeam.candidates.length })}
              </p>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false)
                setDeletingTeam(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTeam}
            >
              {t('teams.deleteTeamCta', 'Delete Team')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Associated offers modal */}
      {offerModalTeam && (
        <Modal
          isOpen={!!offerModalTeam}
          onClose={() => setOfferModalTeam(null)}
          title={t('teams.offersModalTitle', { name: offerModalTeam.name })}
        >
          {offerModalTeam.offers && offerModalTeam.offers.length > 0 ? (
            <div className="space-y-3">
              {offerModalTeam.offers.map((offer) => (
                <div
                  key={offer.id}
                  className="flex items-center justify-between border rounded-lg p-3 bg-muted/30 cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => {
                    setOfferModalTeam(null)
                    navigate(`/offers/${offer.id}`, { state: { from: location.pathname } })
                  }}
                >
                  <div className="mr-4 min-w-0">
                    <p
                      className="text-sm font-medium text-foreground truncate"
                      title={offer.job_title}
                    >
                      {offer.job_title}
                    </p>
                    <p className="text-xs text-muted-foreground">{offer.status}</p>
                  </div>
                  <Badge variant="secondary" className="text-xs capitalize">
                    {offer.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('teams.noOffers')}</p>
          )}
        </Modal>
      )}
    </div>
  )
}

// Helper function to translate server error messages
function translateServerError(errorMessage: string, t: TFunction): string {
  const lowerMessage = errorMessage.toLowerCase()
  
  // Map common server error messages to translation keys
  if (lowerMessage.includes('team with this name already exists') || 
      lowerMessage.includes('group with this name already exists') ||
      lowerMessage.includes('name already exists')) {
    return t('teams.messages.teamNameAlreadyExists')
  }
  
  if (lowerMessage.includes('name') && lowerMessage.includes('required') ||
      lowerMessage.includes('this field is required') && lowerMessage.includes('name')) {
    return t('teams.messages.teamNameRequired')
  }
  
  if (lowerMessage.includes('name') && (lowerMessage.includes('invalid') || lowerMessage.includes('not valid'))) {
    return t('teams.messages.teamNameInvalid')
  }
  
  // If no translation found, return the original message
  return errorMessage
}

// Create Team Modal Component
interface CreateTeamModalProps {
  isOpen: boolean
  onClose: () => void
  onTeamCreated: (team: Team) => void
  offers: Offer[]
  offersLoading: boolean
}

function CreateTeamModal({ isOpen, onClose, onTeamCreated, offers, offersLoading }: CreateTeamModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [formData, setFormData] = useState<{
    name: string
    description: string
    color: string
    offer_ids: number[]
  }>({
    name: '',
    description: '',
    color: '#007bff',
    offer_ids: []
  })
  const [loading, setLoading] = useState(false)
  const [offerSearch, setOfferSearch] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    setLoading(true)
    try {
      const response = await teamsApi.create(formData)
      onTeamCreated(response.data)
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        color: '#007bff',
        offer_ids: []
      })
      setOfferSearch('')
    } catch (error: any) {
      console.error('Error creating team:', error)
      // Extract and translate error message from various response formats
      let errorMessage = t('teams.messages.teamCreateFailed')
      const data = error?.response?.data
      
      if (data) {
        if (typeof data === 'string') {
          errorMessage = translateServerError(data, t)
        } else if (data.error) {
          errorMessage = translateServerError(data.error, t)
        } else if (data.message) {
          errorMessage = translateServerError(data.message, t)
        } else if (data.details) {
          errorMessage = translateServerError(data.details, t)
        } else if (typeof data === 'object') {
          // Handle DRF validation errors
          const parts: string[] = []
          Object.entries(data).forEach(([field, value]) => {
            if (Array.isArray(value)) {
              const translatedErrors = value.map((v: string) => translateServerError(v, t))
              const fieldLabel = field === 'name' ? t('teams.teamName', 'Group name') : field
              parts.push(`${fieldLabel}: ${translatedErrors.join(', ')}`)
            } else if (typeof value === 'string') {
              const fieldLabel = field === 'name' ? t('teams.teamName', 'Group name') : field
              parts.push(`${fieldLabel}: ${translateServerError(value, t)}`)
            }
          })
          if (parts.length) {
            errorMessage = parts.join(' | ')
          }
        }
      }
      
      addToast({
        type: 'error',
        title: t('common.error'),
        message: errorMessage,
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      color: '#007bff',
      offer_ids: []
    })
    setOfferSearch('')
    onClose()
  }

  const handleOfferToggle = (offerId: number, checked: boolean | 'indeterminate') => {
    setFormData(prev => {
      const isChecked = checked === true
      const nextIds = isChecked
        ? Array.from(new Set([...prev.offer_ids, offerId]))
        : prev.offer_ids.filter(id => id !== offerId)
      return { ...prev, offer_ids: nextIds }
    })
  }

  const filteredOffers = offers.filter(offer => {
    const title = offer.job_title || offer.title || ''
    return title.toLowerCase().includes(offerSearch.toLowerCase())
  })

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('teams.createTitle', 'Create New Team')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium">{t('teams.fields.name', 'Team Name')}</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t('teams.placeholders.name', 'Enter team name')}
            className="mt-1"
            required
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">{t('teams.fields.description', 'Description')}</label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder={t('teams.placeholders.description', 'Enter team description (optional)')}
            className="mt-1"
          />
        </div>
        
        {/* Color Selection */}
        <div>
          <label className="text-sm font-medium mb-2 block">{t('teams.fields.color', 'Team Color')}</label>
          <div className="flex items-center space-x-2">
            {[
              '#007bff', '#28a745', '#dc3545', '#ffc107', 
              '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'
            ].map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color }))}
                className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                  formData.color === color ? 'border-gray-800 dark:border-gray-200 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                style={{ backgroundColor: color }}
                title={t('teams.colorSwatch', 'Color')}
              />
            ))}
            
            {/* Custom Color */}
            <div className="relative">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-6 h-6 rounded-full border-2 border-gray-200 dark:border-gray-700 cursor-pointer opacity-0 absolute"
                style={{ 
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              <div 
                className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                  !['#007bff', '#28a745', '#dc3545', '#ffc107', 
                    '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'].includes(formData.color)
                    ? 'border-gray-800 dark:border-gray-200 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                style={{ backgroundColor: formData.color }}
                title={t('teams.customColor', 'Custom Color')}
              />
            </div>
          </div>
        </div>

       
        
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit"
            disabled={!formData.name.trim() || loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.creating', 'Creating...')}
              </>
            ) : (
              <>
                <Users className="h-4 w-4 mr-2" />
                {t('teams.createCta', 'Create Team')}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// Edit Team Modal Component
interface EditTeamModalProps {
  isOpen: boolean
  onClose: () => void
  team: Team
  onTeamUpdated: (team: Team) => void
  offers: Offer[]
  offersLoading: boolean
}

function EditTeamModal({ isOpen, onClose, team, onTeamUpdated, offers, offersLoading }: EditTeamModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [formData, setFormData] = useState<{
    name: string
    description: string
    color: string
    offer_ids: number[]
  }>({
    name: team.name,
    description: team.description || '',
    color: team.color,
    offer_ids: team.offers?.map((offer) => offer.id) || []
  })
  const [loading, setLoading] = useState(false)
  const [offerSearch, setOfferSearch] = useState('')

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: team.name,
        description: team.description || '',
        color: team.color,
        offer_ids: team.offers?.map((offer) => offer.id) || []
      })
      setOfferSearch('')
    }
  }, [isOpen, team])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    setLoading(true)
    try {
      const response = await teamsApi.update(team.id, formData)
      onTeamUpdated(response.data)
    } catch (error) {
      console.error('Error updating team:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teams.messages.teamUpdateFailed'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleOfferToggle = (offerId: number, checked: boolean | 'indeterminate') => {
    setFormData(prev => {
      const isChecked = checked === true
      const nextIds = isChecked
        ? Array.from(new Set([...(prev.offer_ids || []), offerId]))
        : (prev.offer_ids || []).filter(id => id !== offerId)
      return { ...prev, offer_ids: nextIds }
    })
  }

  const filteredOffers = offers.filter(offer => {
    const title = offer.job_title || offer.title || ''
    return title.toLowerCase().includes(offerSearch.toLowerCase())
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('teams.editTitle', 'Edit Team')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium">{t('teams.fields.name', 'Team Name')}</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t('teams.placeholders.name', 'Enter team name')}
            className="mt-1"
            required
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">{t('teams.fields.description', 'Description')}</label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder={t('teams.placeholders.description', 'Enter team description (optional)')}
            className="mt-1"
          />
        </div>
        
        {/* Color Selection */}
        <div>
          <label className="text-sm font-medium mb-2 block">{t('teams.fields.color', 'Team Color')}</label>
          <div className="flex items-center space-x-2">
            {[
              '#007bff', '#28a745', '#dc3545', '#ffc107', 
              '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'
            ].map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color }))}
                className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                  formData.color === color ? 'border-gray-800 dark:border-gray-200 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                style={{ backgroundColor: color }}
                title={t('teams.colorSwatch', 'Color')}
              />
            ))}
            
            {/* Custom Color */}
            <div className="relative">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-6 h-6 rounded-full border-2 border-gray-200 dark:border-gray-700 cursor-pointer opacity-0 absolute"
                style={{ 
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              <div 
                className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                  !['#007bff', '#28a745', '#dc3545', '#ffc107', 
                    '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'].includes(formData.color)
                    ? 'border-gray-800 dark:border-gray-200 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                style={{ backgroundColor: formData.color }}
                title={t('teams.customColor', 'Custom Color')}
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit"
            disabled={!formData.name.trim() || loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.updating', 'Updating...')}
              </>
            ) : (
              <>
                <Edit3 className="h-4 w-4 mr-2" />
                {t('teams.updateCta', 'Update Team')}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
